<template>
    <div class="custom-fields-container">
        <div class="main-content">
            <h1>Volunteer Custom Fields</h1>
            <div class="sticky-header">
                <div class="search-container">
                    <i class="fas fa-search search-icon"></i>
                    <input
                            ref="searchInputRef"
                            type="text"
                            v-model="searchQuery"
                            placeholder="Search fields or categories..."
                            class="search-input"
                    >
                </div>
                <button class="mobile-menu-button" @click="toggleMenu">
                    <Icon name="bars" size="xl" color="var(--primary-700)"/>
                </button>
                <div class="header-actions" :class="{ 'mobile-menu-open': menuOpen }" ref="headerActionsRef" @focusout="handleMenuBlur"
                     @keydown="handleMenuKeydown">
                    <div class="desktop-actions">
                        <button v-if="!isSorted" @click="sortGroups" class="toggle-button" title="Sort Alphabetically">
                            <Icon name="sort-alpha-down" size="xl" color="var(--primary-700)" hover="var(--grayscale-600)"></Icon>
                        </button>
                        <button v-if="isSorted" @click="resetSort" class="toggle-button" title="Reset Sort">
                            <Icon name="fa-sharp fa-solid fa-rotate-left" size="xl" color="var(--primary-700)" hover="var(--grayscale-600)"></Icon>
                        </button>
                        <div class="separator"></div>
                        <button @click="toggleAll(true)" class="toggle-button" title="Expand All">
                            <Icon name="arrow-up-expand" size="xl" color="var(--primary-700)" hover="var(--grayscale-600)"></Icon>
                        </button>
                        <div class="separator"></div>
                        <button @click="toggleAll(false)" class="toggle-button" title="Hide All">
                            <Icon name="arrow-down-expand" size="xl" color="var(--primary-700)" hover="var(--grayscale-500)"></Icon>
                        </button>
                    </div>
                    <div class="mobile-actions">
                        <button v-for="item in dropdownItems" :key="item.label" @click="handleMenuAction(item.action)" class="dropdown-item">
                            <Icon :name="item.icon" size="xl" color="var(--primary-700)"/>
                            <span>{{ item.label }}</span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="accordion-container">
                <div v-for="section in filteredSections" :key="section.id" class="accordion-item" :class="{ 'section-is-editing': section.isEditing }"
                     @keydown.esc.prevent="cancelSectionEdit(section)">
                    <div title="Edit Section"
                         class="accordion-header"
                         @click="!section.isEditing && toggleAccordion(section)"
                         :class="{'header-open': section.isOpen}"
                         role="button"
                         tabindex="0"
                         @keydown.enter.prevent="!section.isEditing && toggleAccordion(section)">
                        <div class="header-left-content">
                            <h2 class="header-title">{{ section.title }}</h2>
                            <transition name="toast-inline">
                                <div v-if="section.toast" class="toast-message-inline" :class="'toast-' + section.toast.type">
                                    <Icon v-if="section.toast.type === 'success'" name="check-circle" color="var(--success-500)" size="lg"/>
                                    <Icon v-if="section.toast.type === 'error'" name="exclamation-circle" color="var(--danger-500)" size="lg"/>
                                    <span>{{ section.toast.message }}</span>
                                </div>
                            </transition>
                        </div>
                        <div class="header-actions-accordion">
                            <button v-show="section.isEditing" @click.stop="cancelSectionEdit(section)" @keydown.enter.prevent="cancelSectionEdit(section)"
                                    class="button-cancel">
                                Cancel
                            </button>
                            <button v-show="section.isEditing" @click.stop="saveSectionEdit(section)" class="button-save"
                                    :ref="el => { if(el) saveButtonRefs[section.id] = el }">
                                Save
                            </button>
                            <button v-show="!section.isEditing" @click.stop="enableSectionEdit(section)" @keydown.enter.prevent="enableSectionEdit(section)"
                                    class="edit-section-button" title="Edit Section">
                                <Icon name="pencil" size="xl" color="var(--primary-400)" hover="var(--primary-700)"></Icon>
                            </button>
                            <button v-if="!section.isEditing"
                                    @click.stop.prevent="toggleAccordion(section)"
                                    class="chevron-button"
                                    :title="section.isOpen ? 'Close Section' : 'Open Section'">
                                <Icon name="chevron-down"
                                      size="xl" color="var(--primary-400)"
                                      hover="var(--primary-700)" class="chevron-icon"
                                      :class="{ 'rotated': section.isOpen }"/>
                            </button>
                        </div>
                    </div>
                    <div v-show="section.isOpen" class="accordion-content">
                        <div class="grid-container">
                            <div v-for="(field, fieldIndex) in section.fields" :key="field.id"
                                 :class="['field-container', field.colSpan === 2 ? 'col-span-2' : 'col-span-1']"
                                 :id="'field-container-' + field.id"
                                 @keydown.tab="section.isEditing && fieldIndex === section.fields.length - 1 ? handleLastFieldTab(section, $event) : null">
                                <div v-if="!field.isEditing"
                                     :ref="el => fieldDisplayRefs[field.id] = el"
                                     class="field-display"
                                     :class="{ 'clickable': !section.isEditing }"
                                     @click="!section.isEditing && enableFieldEdit(field)"
                                     :tabindex="!section.isEditing ? 0 : -1"
                                     @keydown.enter.prevent="!section.isEditing && enableFieldEdit(field)">
                                    <label class="field-label" :class="{'required-field': field.required}" :for="'field-display-' + field.id">{{
                                            field.label
                                        }}</label>
                                    <div class="field-value" :id="'field-display-' + field.id">
                                        <template v-if="field.value">
                                            <template v-if="Array.isArray(field.value)">{{ field.value.join(', ') }}</template>
                                            <template v-else>{{ field.value }}</template>
                                        </template>
                                        <template v-else>
                                            <span class="placeholder-text">—</span>
                                        </template>
                                    </div>
                                    <div class="edit-actions" v-if="!section.isEditing">
                                        <button @click.stop="enableFieldEdit(field)" @keydown.enter.prevent="enableFieldEdit(field)" class="button-edit-inline"
                                                title="Edit Field">
                                            <Icon name="pencil" size="lg" color="var(--primary-400)"></Icon>
                                        </button>
                                    </div>
                                </div>
                                <div v-else class="field-edit field-display"
                                     @keydown.tab="handleFieldTab(section, fieldIndex, $event)"
                                     @keydown.enter="handleEnterKey($event, field, section)">
                                    <label class="field-label" :class="{'required-field': field.required}" :for="'field-input-' + field.id">{{
                                            field.label
                                        }}</label>

                                    <!-- Special layout for dropdowns -->
                                    <div v-if="field.type === 'select' || (field.options && !field.multiselect && field.type !== 'boolean')" class="input-with-actions">
                                        <div class="select-wrapper">
                                            <select :id="'field-input-' + field.id"
                                                    :ref="el => inputRefs[field.id] = el"
                                                    v-model="field.value"
                                                    :class="['input-field', field.error ? 'input-error' : '']">
                                                <option v-for="option in field.options" :key="option" :value="option">{{ option }}</option>
                                            </select>
                                            <Icon name="chevron-down" class="select-chevron" size="lg" color="var(--grayscale-700)"></Icon>
                                        </div>
                                        <div v-if="!section.isEditing" class="edit-actions">
                                            <button @click.stop="cancelFieldEdit(field)" @keydown.esc="cancelFieldEdit(field)"
                                                    @keydown.enter.prevent="cancelFieldEdit(field)" class="button-cancel-inline" title="Cancel" tabindex="0">
                                                <Icon name="close" color="var(--danger-500)" size="xl"/>
                                            </button>
                                            <button @click.stop="saveFieldEdit(field)" class="button-save-inline" title="Save" tabindex="0">
                                                <Icon name="check" color="var(--success-500)" size="xl"/>
                                            </button>
                                        </div>
                                    </div>

                                    <!-- Original layout for all other fields -->
                                    <template v-else>
                                        <textarea v-if="field.type === 'textarea'"
                                                  :id="'field-input-' + field.id"
                                                  :ref="el => inputRefs[field.id] = el"
                                                  v-model="field.value"
                                                  :class="['input-field', field.error ? 'input-error' : '']">
                                        </textarea>
                                        <div v-else-if="field.multiselect" class="checkbox-group" :id="'field-input-' + field.id">
                                            <label v-for="(option, index) in field.options" :key="option" class="checkbox-label"
                                                   :for="'field-input-' + field.id + '-' + index">
                                                <input type="checkbox"
                                                       :id="'field-input-' + field.id + '-' + index"
                                                       :value="option"
                                                       v-model="field.value">
                                                {{ option }}
                                            </label>
                                        </div>
                                        <div v-else-if="field.type === 'boolean' || (field.options && field.options.length === 2 && field.options.includes('Yes') && field.options.includes('No'))"
                                             class="radio-group" :id="'field-input-' + field.id">
                                            <template v-if="field.type === 'boolean'">
                                                <label class="radio-label" :for="'field-input-' + field.id + '-yes'">
                                                    <input type="radio" :id="'field-input-' + field.id + '-yes'" :name="field.id" :value="true"
                                                           v-model="field.value"> Yes
                                                </label>
                                                <label class="radio-label" :for="'field-input-' + field.id + '-no'">
                                                    <input type="radio" :id="'field-input-' + field.id + '-no'" :name="field.id" :value="false"
                                                           v-model="field.value"> No
                                                </label>
                                            </template>
                                            <template v-else>
                                                <label v-for="(option, index) in field.options" :key="option" class="radio-label"
                                                       :for="'field-input-' + field.id + '-' + index">
                                                    <input type="radio"
                                                           :id="'field-input-' + field.id + '-' + index"
                                                           :name="field.id"
                                                           :value="option"
                                                           v-model="field.value">
                                                    {{ option }}
                                                </label>
                                            </template>
                                        </div>
                                        <input v-else
                                               :id="'field-input-' + field.id"
                                               :ref="el => inputRefs[field.id] = el"
                                               :type="field.type === 'email' ? 'email' : 'text'"
                                               v-model="field.value"
                                               :class="['input-field', field.error ? 'input-error' : '']">
                                        <div v-if="!section.isEditing" class="edit-actions">
                                            <button @click.stop="cancelFieldEdit(field)" @keydown.esc="cancelFieldEdit(field)"
                                                    @keydown.enter.prevent="cancelFieldEdit(field)" class="button-cancel-inline" title="Cancel" tabindex="0">
                                                <Icon name="close" color="var(--danger-500)" size="xl"/>
                                            </button>
                                            <button @click.stop="saveFieldEdit(field)" class="button-save-inline" title="Save" tabindex="0">
                                                <Icon name="check" color="var(--success-500)" size="xl"/>
                                            </button>
                                        </div>
                                    </template>
                                    <p v-if="field.error" class="error-message"><i class="fas fa-exclamation-circle"></i>{{ field.error }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="filteredSections.length === 0" class="no-results">
                    <i class="fas fa-search"></i>
                    <p>No sections or fields match your search.</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    ref, computed, onMounted, nextTick, onUnmounted, watch
} from 'vue';
import Icon from '../common/icon/Icon.vue';
import { INITIAL_DATA } from './customFieldsMock.js';

export default {
    name: 'CustomFields',
    components: {
        Icon,
    },
    setup() {
        const sections = ref(JSON.parse(JSON.stringify(INITIAL_DATA))
            .map((sec) => ({
                ...sec,
                isOpen: sec.isOpen || false,
                isEditing: false,
                toast: null,
                fields: sec.fields.map((f) => ({
                    ...f,
                    isEditing: false,
                    originalValue: f.value,
                    error: null
                }))
            })));

        const searchQuery = ref('');
        const manuallyClosedSectionsInSearch = ref([]);
        const searchInputRef = ref(null);
        const isSorted = ref(false);
        const menuOpen = ref(false);
        const headerActionsRef = ref(null);
        let initialOrder = [];

        const inputRefs = ref({});
        const fieldDisplayRefs = ref({});
        const saveButtonRefs = ref({});
        const editingSectionId = ref(null);
        const editingFieldId = ref(null);

        const toggleMenu = () => {
            menuOpen.value = !menuOpen.value;
            if (menuOpen.value) {
                nextTick(() => {
                    const firstItem = headerActionsRef.value?.querySelector('.mobile-actions button');
                    if (firstItem) {
                        firstItem.focus();
                    }
                });
            }
        };

        const handleMenuBlur = (event) => {
            if (menuOpen.value && !headerActionsRef.value?.contains(event.relatedTarget)) {
                menuOpen.value = false;
            }
        };

        const handleMenuKeydown = (event) => {
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
            event.preventDefault();

            const items = Array.from(headerActionsRef.value.querySelectorAll('.mobile-actions button'));
            if (!items.length) return;

            const currentIndex = items.findIndex((item) => item === document.activeElement);

            let nextIndex;
            if (currentIndex === -1) {
                nextIndex = event.key === 'ArrowDown' ? 0 : items.length - 1;
            } else {
                if (event.key === 'ArrowDown') {
                    nextIndex = (currentIndex + 1) % items.length;
                } else { // ArrowUp
                    nextIndex = (currentIndex - 1 + items.length) % items.length;
                }
            }

            items[nextIndex]?.focus();
        };

        const filteredSections = computed(() => {
            if (!searchQuery.value) return sections.value;
            const query = searchQuery.value.toLowerCase();
            return sections.value.map((section) => {
                const matchTitle = section.title.toLowerCase()
                    .includes(query);
                const matchedFields = section.fields.filter((f) => f.label.toLowerCase()
                    .includes(query) || (f.value && String(f.value)
                    .toLowerCase()
                    .includes(query)));
                if (matchTitle || matchedFields.length > 0) {
                    return {
                        ...section,
                        fields: matchTitle ? section.fields : matchedFields
                    };
                }
                return null;
            })
                .filter(Boolean);
        });

        const cancelFieldEdit = (fieldToCancel, options = { focusSelf: true }) => {
            fieldToCancel.value = fieldToCancel.originalValue;
            fieldToCancel.isEditing = false;
            fieldToCancel.error = null;
            editingFieldId.value = null;
            if (options.focusSelf) {
                nextTick(() => {
                    const fieldElement = fieldDisplayRefs.value[fieldToCancel.id];
                    if (fieldElement) {
                        fieldElement.focus();
                    }
                });
            }
        };

        const cancelSectionEdit = (sectionToEdit) => {
            const section = sections.value.find((s) => s.id === sectionToEdit.id);
            if (section) {
                section.isEditing = false;
                editingSectionId.value = null;
                section.fields.forEach((field) => {
                    field.value = field.originalValue;
                    field.isEditing = false;
                    field.error = null;
                });
            }
        };

        const toggleAccordion = (clickedSection) => {
            const section = sections.value.find((s) => s.id === clickedSection.id);
            if (!section) return;

            // If the section is currently open and we're about to close it, cancel any active edits.
            if (section.isOpen) {
                if (section.isEditing) {
                    cancelSectionEdit(section);
                } else {
                    section.fields.forEach((field) => {
                        if (field.isEditing) {
                            cancelFieldEdit(field, { focusSelf: false });
                        }
                    });
                }
            }

            section.isOpen = !section.isOpen;

            // If search is active, manage the manually closed state
            if (searchQuery.value) {
                const index = manuallyClosedSectionsInSearch.value.indexOf(section.id);
                if (section.isOpen) {
                    // If we just opened it, remove it from the manually closed list
                    if (index > -1) {
                        manuallyClosedSectionsInSearch.value.splice(index, 1);
                    }
                } else {
                    // If we just closed it, add it to the list
                    if (index === -1) {
                        manuallyClosedSectionsInSearch.value.push(section.id);
                    }
                }
            }
        };

        const sortGroups = () => {
            sections.value.sort((a, b) => a.title.localeCompare(b.title));
            isSorted.value = true;
        };

        const resetSort = () => {
            sections.value.sort((a, b) => initialOrder.indexOf(a.id) - initialOrder.indexOf(b.id));
            isSorted.value = false;
        };

        const toggleAll = (state) => {
            sections.value.forEach((section) => {
                section.isOpen = state;
            });
        };

        const focusNextField = (fromField) => {
            let fromSection;
            let fromFieldIndex;
            for (const section of sections.value) {
                const fieldIndex = section.fields.findIndex((f) => f.id === fromField.id);
                if (fieldIndex !== -1) {
                    fromSection = section;
                    fromFieldIndex = fieldIndex;
                    break;
                }
            }

            if (fromSection && fromFieldIndex + 1 < fromSection.fields.length) {
                const nextField = fromSection.fields[fromFieldIndex + 1];
                nextTick(() => {
                    const nextFieldElement = fieldDisplayRefs.value[nextField.id];
                    if (nextFieldElement) {
                        nextFieldElement.focus();
                    }
                });
            }
        };

        const enableSectionEdit = (sectionToEdit) => {
            const otherEditingSection = sections.value.find((s) => s.isEditing && s.id !== sectionToEdit.id);
            if (otherEditingSection) {
                cancelSectionEdit(otherEditingSection);
            }
            sections.value.forEach((s) => {
                s.fields.forEach((f) => {
                    if (f.isEditing && !s.isEditing) {
                        cancelFieldEdit(f, { focusSelf: false });
                    }
                });
            });

            const section = sections.value.find((s) => s.id === sectionToEdit.id);
            if (section) {
                section.isEditing = true;
                editingSectionId.value = section.id;
                editingFieldId.value = null;
                if (!section.isOpen) {
                    section.isOpen = true;
                }
                section.fields.forEach((f) => {
                    f.originalValue = f.value;
                    f.isEditing = true;
                });
            }
        };

        const saveSectionEdit = async (sectionToEdit) => {
            const section = sections.value.find((s) => s.id === sectionToEdit.id);
            if (!section) return;

            let hasValidationError = false;
            section.fields.forEach((field) => {
                if (field.type === 'email' && field.value && !field.value.includes('@')) {
                    field.error = 'Please enter a valid email format.';
                    hasValidationError = true;
                } else {
                    field.error = null;
                }
            });

            if (hasValidationError) {
                return;
            }

            try {
                // Simulate API call
                await new Promise((resolve, reject) => {
                    setTimeout(() => {
                        const hasServerError = section.fields.some((f) => f.value === 'Server Error');
                        if (hasServerError) {
                            reject(new Error('Simulated server error triggered by input.'));
                        } else {
                            resolve();
                        }
                    }, 500);
                });

                // Success
                section.isEditing = false;
                editingSectionId.value = null;
                section.fields.forEach((field) => {
                    field.originalValue = field.value;
                    field.isEditing = false;
                });
                section.toast = { message: 'Section updated!', type: 'success' };
                setTimeout(() => {
                    section.toast = null;
                }, 3000);
            } catch (error) {
                // Error
                section.toast = { message: 'Error saving section.', type: 'error' };
                setTimeout(() => {
                    section.toast = null;
                }, 3000);
            }
        };

        const enableFieldEdit = (fieldToEdit) => {
            const editingSection = sections.value.find((s) => s.isEditing);
            if (editingSection) {
                cancelSectionEdit(editingSection);
            }
            sections.value.forEach((s) => {
                s.fields.forEach((f) => {
                    if (f.isEditing && f.id !== fieldToEdit.id) {
                        cancelFieldEdit(f, { focusSelf: false });
                    }
                });
            });

            fieldToEdit.isEditing = true;
            editingFieldId.value = fieldToEdit.id;
            editingSectionId.value = null;
            nextTick(() => {
                const input = inputRefs.value[fieldToEdit.id];
                if (input) {
                    input.focus();
                }
            });
        };

        const saveFieldEdit = async (fieldToSave, options = { focusNext: true }) => {
            if (JSON.stringify(fieldToSave.value) === JSON.stringify(fieldToSave.originalValue)) {
                cancelFieldEdit(fieldToSave, { focusSelf: !options.focusNext });
                if (options.focusNext) {
                    focusNextField(fieldToSave);
                }
                return;
            }

            let hasValidationError = false;
            if (fieldToSave.type === 'email' && fieldToSave.value && !fieldToSave.value.includes('@')) {
                fieldToSave.error = 'Please enter a valid email format.';
                hasValidationError = true;
            } else {
                fieldToSave.error = null;
            }

            if (hasValidationError) {
                return;
            }

            try {
                // Simulate API call. This will be replaced with a real API call.
                await new Promise((resolve, reject) => {
                    setTimeout(() => {
                        if (fieldToSave.value === 'Server Error') {
                            reject(new Error('Simulated server error triggered by input.'));
                        } else {
                            resolve();
                        }
                    }, 500);
                });

                // Success
                fieldToSave.originalValue = fieldToSave.value;
                fieldToSave.isEditing = false;
                editingFieldId.value = null;

                const section = sections.value.find((s) => s.fields.some((f) => f.id === fieldToSave.id));
                if (section) {
                    section.toast = { message: 'Field updated!', type: 'success' };
                    setTimeout(() => {
                        section.toast = null;
                    }, 3000);
                }

                if (options.focusNext) {
                    focusNextField(fieldToSave);
                }
            } catch (error) {
                // Error
                fieldToSave.error = 'Could not save changes. Please try again.';
                const section = sections.value.find((s) => s.fields.some((f) => f.id === fieldToSave.id));
                if (section) {
                    section.toast = { message: 'Error saving field.', type: 'error' };
                    setTimeout(() => {
                        section.toast = null;
                    }, 3000);
                }
            }
        };

        const dropdownItems = computed(() => {
            const items = [];
            if (!isSorted.value) {
                items.push({
                    label: 'Sort Alphabetically',
                    icon: 'sort-alpha-down',
                    action: 'sort'
                });
            } else {
                items.push({
                    label: 'Reset Sort',
                    icon: 'fa-sharp fa-solid fa-rotate-left',
                    action: 'resetSort'
                });
            }
            items.push({
                label: 'Expand All',
                icon: 'arrow-up-expand',
                action: 'expandAll'
            });
            items.push({
                label: 'Hide All',
                icon: 'arrow-down-expand',
                action: 'hideAll'
            });
            return items;
        });

        const handleEnter = (event, section) => {
            if (event.target.tagName === 'TEXTAREA') {
                return;
            }
            event.preventDefault();
            this.saveSectionEdit(section);
        };

        const handleMenuAction = (action) => {
            if (action === 'sort') {
                sortGroups();
            } else if (action === 'resetSort') {
                resetSort();
            } else if (action === 'expandAll') {
                toggleAll(true);
            } else if (action === 'hideAll') {
                toggleAll(false);
            }
            menuOpen.value = false;
        };

        const handleLastFieldTab = (section, event) => {
            if (event.shiftKey) return;

            const container = event.currentTarget;
            const focusable = Array.from(container.querySelectorAll('input:not([type=hidden]), textarea, select, [tabindex]:not([tabindex="-1"])'));
            const lastFocusableElement = focusable[focusable.length - 1];

            if (event.target === lastFocusableElement) {
                event.preventDefault();
                const saveButton = saveButtonRefs.value[section.id];
                if (saveButton) {
                    saveButton.focus();
                }
            }
        };

        const handleFieldTab = (section, currentIndex, event) => {
            const currentField = section.fields[currentIndex];
            if (section.isEditing || !currentField.isEditing) {
                return;
            }

            const container = event.currentTarget;
            const focusable = Array.from(container.querySelectorAll('input:not([type=hidden]), textarea, select, button, [tabindex]:not([tabindex="-1"])'));
            const currentFocusIndex = focusable.indexOf(event.target);
            const direction = event.shiftKey ? -1 : 1;

            if (direction === 1 && currentFocusIndex < focusable.length - 1) {
                return;
            }
            if (direction === -1 && currentFocusIndex > 0) {
                return;
            }

            const nextIndex = currentIndex + direction;

            if (nextIndex >= 0 && nextIndex < section.fields.length) {
                event.preventDefault();
                saveFieldEdit(currentField, { focusNext: false });

                if (currentField.error) {
                    nextTick(() => {
                        const input = inputRefs.value[currentField.id];
                        if (input) input.focus();
                    });
                    return;
                }

                const nextField = section.fields[nextIndex];
                enableFieldEdit(nextField);
            } else {
                saveFieldEdit(currentField);
            }
        };

        const handleEnterKey = (event, field, section) => {
            // Allow Shift+Enter for new lines in textareas
            if (field.type === 'textarea' && event.shiftKey) {
                return;
            }
            event.preventDefault();
            if (section.isEditing) {
                saveSectionEdit(section);
            } else if (field.isEditing) {
                saveFieldEdit(field);
            }
        };

        const handleKeydown = (event) => {
            const isSaveShortcut = (event.ctrlKey || event.metaKey) && event.key === 's';

            if (event.key === 'Escape') {
                if (editingSectionId.value || editingFieldId.value) {
                    event.preventDefault();
                    if (editingSectionId.value) {
                        const sectionToCancel = sections.value.find((s) => s.id === editingSectionId.value);
                        if (sectionToCancel) {
                            cancelSectionEdit(sectionToCancel);
                        }
                    } else if (editingFieldId.value) {
                        let fieldToCancel = null;
                        sections.value.forEach((s) => {
                            const found = s.fields.find((f) => f.id === editingFieldId.value);
                            if (found) fieldToCancel = found;
                        });
                        if (fieldToCancel) {
                            cancelFieldEdit(fieldToCancel);
                        }
                    }
                }
                return;
            }

            if (isSaveShortcut) {
                event.preventDefault();
                if (editingSectionId.value) {
                    const sectionToSave = sections.value.find((s) => s.id === editingSectionId.value);
                    if (sectionToSave) saveSectionEdit(sectionToSave);
                } else if (editingFieldId.value) {
                    let fieldToSave = null;
                    sections.value.forEach((s) => {
                        const found = s.fields.find((f) => f.id === editingFieldId.value);
                        if (found) fieldToSave = found;
                    });
                    if (fieldToSave) saveFieldEdit(fieldToSave);
                }
            }
        };

        watch(searchQuery, (newQuery, oldQuery) => {
            if (newQuery && !oldQuery) {
                manuallyClosedSectionsInSearch.value = [];
            }
            if (!newQuery && oldQuery) {
                manuallyClosedSectionsInSearch.value = [];
            }

            if (newQuery) {
                const query = newQuery.toLowerCase();
                sections.value.forEach((section) => {
                    const matchTitle = section.title.toLowerCase()
                        .includes(query);
                    const hasMatchedFields = section.fields.some((f) => f.label.toLowerCase()
                        .includes(query)
                        || (f.value && String(f.value)
                            .toLowerCase()
                            .includes(query)));

                    if (matchTitle || hasMatchedFields) {
                        if (!manuallyClosedSectionsInSearch.value.includes(section.id)) {
                            section.isOpen = true;
                        }
                    }
                });
            }
        });

        onMounted(() => {
            initialOrder = sections.value.map((s) => s.id);
            window.addEventListener('keydown', handleKeydown);
            searchInputRef.value?.focus();
        });

        onUnmounted(() => {
            window.removeEventListener('keydown', handleKeydown);
        });

        return {
            sections,
            searchQuery,
            searchInputRef,
            filteredSections,
            isSorted,
            inputRefs,
            fieldDisplayRefs,
            saveButtonRefs,
            menuOpen,
            headerActionsRef,
            toggleAccordion,
            sortGroups,
            resetSort,
            toggleAll,
            enableSectionEdit,
            cancelSectionEdit,
            saveSectionEdit,
            enableFieldEdit,
            saveFieldEdit,
            cancelFieldEdit,
            handleEnter,
            toggleMenu,
            dropdownItems,
            handleMenuAction,
            handleLastFieldTab,
            handleFieldTab,
            handleEnterKey,
            handleMenuBlur,
            handleMenuKeydown,
        };
    }
};
</script>

<style scoped>
.custom-fields-container {
    font-family: "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
    color: var(--grayscale-800);
    display: flex;
}

.main-content {
    flex-grow: 1;
    height: 100%;
    padding: var(--space-6) 2rem 2rem;
    overflow-x: clip;
    overflow-y: visible;
}

.main-content h1 {
    font-size: var(--text-size-h2);
    margin: unset;
}

button {
    border: none;
    background: none;
    cursor: pointer;
}

button:hover {
    color: var(--primary-800);
}

.toast-message-inline {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-size-md);
    font-weight: var(--text-weight-medium);
}

.toast-message-inline.toast-success {
    color: var(--success-700);
}

.toast-message-inline.toast-error {
    color: var(--danger-700);
}

.accordion-header:has(.toast-message-inline.toast-success) {
    background-color: var(--success-100);
}

.accordion-header:has(.toast-message-inline.toast-error) {
    background-color: var(--danger-100);
}

.toast-inline-enter-active,
.toast-inline-leave-active {
    transition: all 0.2s ease-in-out;
}

.toast-inline-enter-from,
.toast-inline-leave-to {
    opacity: 0;
    transform: translateX(-10px);
}

.sticky-header {
    position: sticky;
    top: 0;
    z-index: 40;
    padding: var(--space-6);
    margin-bottom: var(--space-6);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.search-container {
    position: relative;
    width: 50%;
}

.search-icon {
    position: absolute;
    left: var(--space-5);
    top: 50%;
    transform: translateY(-50%);
    color: var(--grayscale-400);
}

.search-input {
    width: 100%;
    padding: var(--space-4) var(--space-6) var(--space-4) 2.25rem;
    border: 1px solid var(--grayscale-300);
    border-radius: var(--radius-xl);
}

.search-input:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--primary-500);
    border-color: transparent;
}

.header-actions:not(.accordion-icon) {
    background: var(--surface-default);
    display: flex;
    align-items: center;
    gap: var(--space-4);
}

.desktop-actions {
    display: flex;
    align-items: center;
    gap: var(--space-4);
}

.mobile-actions {
    display: none;
}

.separator {
    width: 1px;
    height: 20px;
    background-color: var(--grayscale-300);
    margin: 0 var(--space-4);
}

.toggle-button:hover {
    text-decoration: underline;
}

.accordion-container {
    border: 1px solid var(--grayscale-100);
}

.accordion-item {
    background-color: var(--surface-default);
    border-bottom: 1px solid var(--grayscale-100);
    overflow: hidden;
}

.accordion-item:last-child {
    border-bottom: none;
}

.accordion-header {
    background: var(--surface-subtle);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-4) var(--space-8);
    cursor: pointer;
}

.accordion-header.header-open {
    border-bottom: 1px solid var(--grayscale-100);
}

.accordion-header:hover {
    background-color: var(--grayscale-50);
}

.header-left-content {
    display: flex;
    align-items: center;
    gap: var(--space-6);
}

.header-title {
    font-size: var(--text-size-h3);
    font-weight: var(--text-weight-semibold);
    color: var(--grayscale-700);
    margin: unset;
}

.header-actions-accordion {
    display: flex;
    align-items: center;
    gap: var(--space-4);
}

.chevron-icon {
    transition: transform 0.3s ease;
}

.chevron-icon.rotated {
    transform: rotate(180deg);
}

.chevron-button {
    padding: var(--space-2);
    border-radius: var(--radius-sm);
}

.chevron-button:hover {
    background: var(--primary-100);
}

.header-actions-accordion .button-cancel,
.header-actions-accordion .button-save {
    padding: var(--space-3) var(--space-6);
}

.edit-section-button {
    border-radius: var(--radius-sm);
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
    aspect-ratio: 1 / 1;
    padding: var(--space-2);
}

.accordion-header:hover .edit-section-button,
.accordion-header:focus .edit-section-button,
.accordion-header:focus-within .edit-section-button {
    opacity: 1;
    visibility: visible;
}

.grid-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-3) 3rem;
    padding-bottom: var(--space-8);
}

.accordion-item.section-is-editing .grid-container {
    background: var(--primary-50);
    border: 1px solid var(--primary-500);
    border-radius: var(--radius-md);
}

.accordion-item.section-is-editing .field-display {
    border-bottom-color: transparent;
}

.accordion-item.section-is-editing .field-edit {
    background: transparent;
    border-color: transparent;
}

.col-span-2 {
    grid-column: span 2 / span 2;
}

.field-display {
    font-size: var(--text-size-lg);
    padding: var(--space-4) var(--space-8);
    border: 1px solid transparent;
    border-bottom: 1px solid var(--border-color-default);
    position: relative;
    outline: 1px solid transparent;
    transition: background-color 0.2s, outline-color 0.2s;
    display: grid;
    grid-template-columns: 250px 1fr;
    align-items: center;
    column-gap: var(--space-8);
    min-height: 50px;
    grid-template-rows: 1fr;
}

.field-display:focus,
.field-edit {
    background: var(--primary-50);
    border: 1px solid var(--primary-500);
}

.field-display.clickable {
    padding-right: var(--space-4);
}

.field-display.clickable:hover {
    background-color: var(--primary-50);
    cursor: pointer;
}

.field-label {
    font-weight: var(--text-weight-medium);
    color: var(--text-color-caption);
    padding: var(--space-1) var(--space-2);
    display: block;
    position: relative;
}

.field-label.required-field::before {
    content: ' *';
    color: var(--danger-500);
    padding-right: var(--space-2);
    position: absolute;
    left: -12px;
}

.field-value {
    color: var(--text-color-body);
    font-weight: var(--text-weight-medium);
    padding-left: var(--space-8);
}

.placeholder-text {
    color: var(--grayscale-400);
}

.input-field {
    padding: var(--space-2);
    border: 1px solid var(--grayscale-300);
    border-radius: var(--radius-xl);
}

.field-edit .input-field {
    padding-left: var(--space-6);
    padding-right: var(--space-4);
}

.input-field:focus {
    box-shadow: 0 0 0 2px var(--primary-500);
    border-color: transparent;
    outline: none;
}

.input-field.input-error {
    border-color: var(--danger-500);
    background-color: var(--danger-50);
}

.field-edit:has(p.error-message) .edit-actions {
    top: 24%;
}

textarea.input-field {
    min-height: 100px;
}

.field-edit select.input-field {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: none;
    padding-right: 2.5rem;
}

.error-message {
    color: var(--danger-500);
    font-size: var(--text-size-xs);
    margin-top: var(--space-2);
    font-weight: var(--text-weight-medium);
}

.error-message i {
    padding-right: var(--space-2);
}

.field-edit .error-message {
    grid-column-start: 1;
}

.no-results {
    text-align: center;
    padding: 3rem 0;
    color: var(--grayscale-500);
}

.no-results i {
    font-size: var(--text-size-h3);
    margin-bottom: var(--space-5);
    opacity: 0.2;
}

.desktop-actions button {
    border: none;
    background: none;
    cursor: pointer;
    padding: var(--space-4);
}

.desktop-actions button:hover {
    background: var(--primary-100);
}

.button-cancel {
    background: var(--surface-default);
    margin: 0 var(--space-4);
    padding: var(--space-4) var(--space-6);
    color: var(--grayscale-600);
    font-weight: var(--text-weight-medium);
    border: 1px solid var(--border-color-disabled);
    border-radius: var(--radius-md);
}

.button-cancel:hover {
    background-color: var(--grayscale-100);
}

.button-save {
    padding: var(--space-4) var(--space-6);
    background-color: var(--primary-500);
    color: var(--surface-default);
    font-weight: var(--text-weight-medium);
    border-radius: var(--radius-md);
}

.button-save:hover {
    background-color: var(--primary-700);
    color: var(--text-color-inverse);
}

.button-save:focus {
    background-color: var(--primary-500);
}

.button-cancel:focus,
.button-save:focus,
.edit-section-button:focus,
.chevron-button:focus,
.button-edit-inline:focus,
.button-save-inline:focus,
.button-cancel-inline:focus,
.toggle-button:focus,
.mobile-menu-button:focus,
.dropdown-item:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--primary-500);
}

.edit-actions {
    position: absolute;
    right: var(--space-9);
    top: 50%;
    transform: translateY(-50%);
    display: flex;
}

.field-display:not(.field-edit) .edit-actions {
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
}

.field-display:not(.field-edit):hover .edit-actions,
.field-display:not(.field-edit):focus-within .edit-actions {
    opacity: 1;
    visibility: visible;
}

.button-edit-inline {
    padding: var(--space-2);
    border-radius: var(--radius-sm);
    border: none;
    background: unset;
}

.button-save-inline, .button-cancel-inline {
    padding: var(--space-2);
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1 / 1;
}

.radio-group, .checkbox-group {
    display: flex;
    gap: var(--space-4);
}

.radio-label, .checkbox-label {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    font-weight: var(--text-weight-regular);
}

.select-wrapper {
    position: relative;
    width: 100%;
}

.select-wrapper .input-field {
    width: 100%;
}

.select-chevron {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
}

.mobile-menu-button {
    display: none;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: var(--space-4);
}

.input-with-actions {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: var(--space-4);
    width: 100%;
}

.input-with-actions > .select-wrapper {
    min-width: 0;
}

.input-with-actions .edit-actions {
    position: static;
    transform: none;
}

@media (max-width: 1280px) {
    .grid-container {
        gap: var(--space-3) 2rem;
    }

    .field-display {
        grid-template-columns: minmax(100px, 200px) 1fr;
    }
}

@media (max-width: 1024px) {
    .main-content {
        padding: var(--space-8);
    }

    .col-span-2 {
        grid-column: 1 / -1;
    }

    .field-display {
        grid-template-columns: 1fr 1fr;
        gap: var(--space-8);
    }

    .field-value {
        padding-left: 0;
    }

    .radio-group, .checkbox-group {
        padding-left: 0;
        flex-wrap: wrap;
    }

    .field-edit {
        gap: var(--space-2);
    }

    .sticky-header {
        gap: var(--space-6);
    }
}

@media (max-width: 850px) {
    .search-container {
        width: 100%;
    }

    .grid-container {
        grid-template-columns: 1fr;
        gap: 0;
    }

    .field-display {
        grid-template-columns: 1fr 1fr;
        gap: var(--space-8);
    }

    .field-value {
        padding-left: 0;
    }

    .radio-group, .checkbox-group {
        padding-left: 0;
        flex-wrap: wrap;
    }

    .field-edit {
        gap: var(--space-2);
    }
}

@media (max-width: 600px) {
    .field-display {
        gap: 0.5rem;
        grid-template-columns: 1fr;
        padding-bottom: var(--space-5);
    }

    .sticky-header {
        flex-wrap: wrap;
        justify-content: space-between;
        margin-bottom: unset;
        padding-right: var(--space-2);
        position: relative;
        gap: var(--space-8);
    }

    .search-container {
        width: calc(100% - 50px);
        order: 1;
    }

    .mobile-menu-button {
        display: block;
        order: 2;
    }

    .desktop-actions {
        display: none;
    }

    .mobile-actions {
        display: block;
        width: 100%;
    }

    .edit-actions {
        right: var(--space-9);
        top: 70%;
        height: min-content;
    }

    .dropdown-item {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        padding: var(--space-4) var(--space-6);
        cursor: pointer;
        color: var(--grayscale-800);
        width: 100%;
        font-family: inherit;
        font-size: inherit;
    }

    .dropdown-item:hover {
        background-color: var(--grayscale-100);
    }

    .dropdown-item span {
        font-weight: var(--text-weight-medium);
    }

    .header-actions:not(.accordion-icon) {
        display: none;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--space-4);
        padding: var(--space-2) 0;
        border-radius: var(--radius-xs);
        position: absolute;
        top: calc(100% + -18px);
        right: -10px;
        width: auto;
        min-width: 200px;
        background: var(--surface-default);
        box-shadow: var(--shadow-2);
        z-index: 50;
        border: 1px solid var(--grayscale-200);
    }

    .header-actions.mobile-menu-open {
        display: block;
    }

    .header-actions .separator {
        display: none;
    }

    .header-actions-accordion .button-cancel,
    .header-actions-accordion .button-save {
        padding: var(--space-2) var(--space-4);
    }
}
</style>
